import { FC, ReactNode } from 'react'

import { Box, Paper, Typography, alpha } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'

interface DiagnosisNoticePanelProps {
  /** Accent colour driving the border, tint and icon badge. */
  accent: string
  icon: ReactNode
  /** Small uppercase heading above the body copy. */
  title: string
  body: ReactNode
  /** Optional trailing caption line. */
  caption?: ReactNode
  /** Border tint strength. Portfolio summaries sit lighter than warnings. */
  borderOpacity?: number
  backgroundOpacity?: number
  iconOpacity?: number
}

/**
 * Lead-in panel used by the diagnosis sections: a circular accent icon badge
 * beside an overline heading, body copy and an optional caption.
 *
 * Shared by the portfolio summary and the "no profile on file" disclaimers on
 * both the single-game and comparison diagnostics so the three panels cannot
 * drift apart — only the accent, icon and copy differ between them.
 */
export const DiagnosisNoticePanel: FC<DiagnosisNoticePanelProps> = ({
  accent,
  icon,
  title,
  body,
  caption,
  borderOpacity = 0.3,
  backgroundOpacity = 0.06,
  iconOpacity = 0.16,
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2.25,
      display: 'flex',
      gap: 1.75,
      alignItems: 'flex-start',
      borderColor: alpha(accent, borderOpacity),
      // Solid tint instead of a near-flat gradient: a `linear-gradient(135deg,
      // 8% → 2%)` dithered into visible vertical bands on lower-colour-depth
      // displays, making the panel look like it had a rendering glitch.
      backgroundColor: alpha(accent, backgroundOpacity),
      borderRadius: borderRadiusDefault,
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        backgroundColor: alpha(accent, iconOpacity),
        color: accent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="overline" sx={{ fontWeight: 700, color: accent, letterSpacing: '0.08em', lineHeight: 1.4 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.55, mt: 0.25 }}>
        {body}
      </Typography>
      {caption !== undefined && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.75, lineHeight: 1.5 }}>
          {caption}
        </Typography>
      )}
    </Box>
  </Paper>
)
